import {
  Button, ColorPicker, Input,
  Select,
  Tooltip,
} from 'antd';
import React, {
  Fragment, useEffect, useMemo, useState,
} from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import UserActions from '@Actions/userActions';
import './style.scss';

import H3Text from '@Uilib/h3Text';

import { InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import Checkbox from 'antd/es/checkbox/Checkbox';
import LabelGenerationActions from '@Actions/configurations/labelGenerationActions';
import TenantSelector from '../../../../Common/Selector/TenantSelector';
import generalConfigurationActions from '../../../../../actions/configurations/generalConfigurationActions';
import { setConfigurationValueHelper, transformDataHelper } from './helpers';
import PRZSelect from '../../../../Common/UI/PRZSelect';

const LabelGenerationForm = ({
  user, selectedOrgLabels, selectedLabel, callback, updateLabelGeneration, updateLabelGenerationLoading, createLabelLoading, createLabel, isUpdate, getGeneralConfiguration, getGeneralConfigurationProductsLoading, generalConfigurationProducts,
}) => {
  const [labelConfiguration, setLabelConfiguration] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState('All');
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [labelTemplateIds, setLabelTemplateIds] = useState([]);
  const [adminBatchConfigurations, setAdminBatchConfigurations] = useState([]);
  const [selectedAdminBatchConfigurations, setSelectedAdminBatchConfigurations] = useState([]);

  useEffect(() => {
    getGeneralConfiguration('PRODUCTS');

    if (selectedLabel) {
      setLabelConfiguration(selectedLabel?.configuration || selectedLabel?.default_configuration);
      setTemplateName(selectedLabel?.template_name || selectedLabel?.label_name);
      if (selectedLabel?.tenant_id) setSelectedTenant(selectedLabel?.tenant_id);
      if (selectedLabel?.batch_config?.length) setSelectedAdminBatchConfigurations(selectedLabel?.batch_config);
    }

    if (selectedLabel && selectedOrgLabels) {
      const filteredSelectedLabel = selectedOrgLabels?.filter((label) => label.global_label_id === selectedLabel?.global_label_id);
      const tenants = filteredSelectedLabel?.map((label) => label?.tenant_id);
      setFilteredTenants(tenants);
      const labelTempIds = filteredSelectedLabel?.map((label) => label?.label_template_id);
      setLabelTemplateIds(labelTempIds);
    }
  }, []);

  useEffect(() => {
    if (generalConfigurationProducts) {
      let transformedData = transformDataHelper(generalConfigurationProducts?.batch_management);
      transformedData = transformedData?.filter((data) => data?.enabled && data?.field_name?.toLowerCase() !== 'seller name');
      setAdminBatchConfigurations(transformedData);
    }
  }, [generalConfigurationProducts]);

  const setConfigurationValue = (value, keyName) => {
    const newLabelConfiguration = setConfigurationValueHelper(labelConfiguration, value, keyName);
    setLabelConfiguration(newLabelConfiguration);
  };

  const isValidTemplateName = () => templateName;
  const isValidData = () => !labelConfiguration?.filter((data) => (data?.type !== 'checkbox') && !data?.value).length;

  const handleSubmit = () => {
    setFormSubmitted(true);
    if (isValidTemplateName() && isValidData()) {
      const payload = {
        template_name: templateName,
        org_id: user?.org_id,
        entity_name: selectedLabel?.entity_name,
        configuration: labelConfiguration,
        tenant_id: selectedTenant === 'All' ? null : selectedTenant,
        global_label_id: selectedLabel?.global_label_id,
        label_template_ids: selectedTenant === 'All' ? labelTemplateIds : [],
        batch_config: selectedAdminBatchConfigurations,
      };

      if (isUpdate) {
        payload.label_template_id = selectedLabel?.label_template_id;
        payload.template_ejs = selectedLabel?.template_ejs;
      } else {
        payload.template_ejs = selectedLabel?.html;
      }

      if (isUpdate) {
        updateLabelGeneration(payload, () => { callback(); });
      } else {
        createLabel(payload, () => { callback(); });
      }
    }
  };

  const batchLabelConfigurationOptions = useMemo(() => adminBatchConfigurations?.map((config) => ({
    value: config?.field_name,
    label: config?.field_name,
  })), [adminBatchConfigurations]);

  return (
    <Fragment>
      <div className="label_gen-form__wrapper">
        <div className="ant-row">
          <div className="ant-col-md-24">
            <div style={{ marginBottom: '12px', position: 'relative' }}>
              <div className="orgFormLabel " style={{ display: 'flex', alignItems: 'center' }}>
                <div>
                  {'Template Name'}
                  {' '}
                  <span style={{ color: 'red' }}>*</span>
                </div>
              </div>
              <Input
                placeholder="Enter template name"
                name="template name"
                type="string"
                onChange={(event) => setTemplateName(event?.target?.value)}
                disabled={updateLabelGenerationLoading}
                value={templateName}
                required
                style={{
                  height: '28px',
                  border: '1px solid rgba(68,130,218,.2)',
                  borderRadius: '3px',
                }}
              />
              {formSubmitted && !templateName && (
                <div className="input-error">
                  *Please enter
                  Template Name
                </div>
              )}
            </div>
          </div>
          <div className="ant-col-md-24">
            <div style={{ marginBottom: '12px', position: 'relative' }}>
              <TenantSelector
                selectedTenant={selectedTenant}
                disabledOptions={filteredTenants}
                onChange={(value) => {
                  if (isUpdate) {
                    const newFilteredTenants = filteredTenants.includes(selectedTenant) ? filteredTenants.filter((tenant) => tenant !== selectedTenant) : filteredTenants;
                    setFilteredTenants(newFilteredTenants);
                  }
                  setSelectedTenant(value);
                }}
                showAll
                enableToolTip
                toolTipMessage="Selecting 'All Business Units' will remove any existing templates specific to individual business units. A new template will be created and applied across all business units."
              />
            </div>
          </div>
          {selectedLabel?.entity_name !== 'MANUFACTURING_ORDER' && (
            <div className="ant-col-md-24">
              <div style={{ marginBottom: '12px', position: 'relative' }}>
                <label className="orgFormLabel">
                  Select Label Configuration
                </label>
                <PRZSelect
                  mode="multiple"
                  style={{
                    width: '100%',
                  }}
                  loading={getGeneralConfigurationProductsLoading}
                  placeholder="Configurations"
                  value={selectedAdminBatchConfigurations}
                  onChange={(values) => setSelectedAdminBatchConfigurations(values)}
                  options={batchLabelConfigurationOptions}
                />
              </div>
            </div>
          )}
          {labelConfiguration?.map((labelConfig) => (
            <div className="ant-col-md-24" key={labelConfig?.key_name}>
              {
                labelConfig?.type?.split('_')[0] === 'input' && (
                  <div style={{ marginBottom: '12px', position: 'relative' }}>
                    <div className="orgFormLabel " style={{ display: 'flex', alignItems: 'center' }}>
                      <div>
                        {labelConfig?.label_name}
                        {' '}
                        <span style={{ color: 'red' }}>*</span>
                        {labelConfig?.font_type && (
                          <Tooltip title={labelConfig?.font_type?.toLowerCase()}>
                            <InfoCircleOutlined
                              style={{
                                marginLeft: '5px',
                                color: 'grey',
                                cursor: 'pointer',
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <Input
                      placeholder={`Enter ${labelConfig?.label_name}`}
                      suffix={labelConfig?.font_type?.toLowerCase()}
                      name={labelConfig?.label_name}
                      type={labelConfig?.type === 'input_number' ? 'number' : 'string'}
                      onChange={(event) => setConfigurationValue(event?.target?.value, labelConfig?.key_name)}
                      disabled={updateLabelGenerationLoading}
                      value={labelConfig?.value}
                      required
                      style={{
                        height: '28px',
                        border: '1px solid rgba(68,130,218,.2)',
                        borderRadius: '3px',
                      }}
                    />
                    {formSubmitted && !labelConfig?.value && (
                      <div className="input-error">
                        *Please enter
                        {` ${labelConfig?.label_name}`}
                      </div>
                    )}
                  </div>
                )
              }
              {
                labelConfig?.type === 'checkbox' && (
                  <div style={{ marginBottom: '12px', position: 'relative' }}>
                    <div className="orgFormLabel " style={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={labelConfig?.value}
                        disabled={updateLabelGenerationLoading}
                        onChange={() => setConfigurationValue(!labelConfig?.value, labelConfig?.key_name)}
                      >
                        {labelConfig?.label_name}
                      </Checkbox>
                    </div>
                  </div>
                )
              }
              {
                labelConfig?.type?.split('_')[0] === 'color' && (
                  <div style={{ marginTop: '5px', marginBottom: '10px' }}>
                    <H3Text required text={labelConfig?.label_name} className="custom-drawer__title orgInputContainer orgFormLabel" />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <ColorPicker
                        defaultValue={labelConfig?.value}
                        onChange={(color) => {
                          setConfigurationValue(color.toHexString(), labelConfig?.key_name);
                        }}
                        showText
                      />
                      <Input
                        suffix="hex"
                        name={labelConfig?.label_name}
                        type="string"
                        disabled
                        value={labelConfig?.value}
                        style={{
                          height: '28px',
                          border: '1px solid rgba(68,130,218,.2)',
                          borderRadius: '3px',
                        }}
                      />

                    </div>
                  </div>
                )
              }
              {
                labelConfig?.type?.split('_')[0] === 'dropdown' && (
                  <div style={{ marginTop: '5px', marginBottom: '10px' }}>
                    <H3Text required text={labelConfig?.label_name} className="custom-drawer__title orgInputContainer orgFormLabel" />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <PRZSelect
                        value={labelConfig?.value}
                        style={{
                          width: '100%',
                        }}
                        onChange={(value) => setConfigurationValue(value, labelConfig?.key_name)}
                        options={
                          labelConfig?.drop_down_values?.map((option) => ({
                            value: option,
                            label: option,
                          }))
                        }
                      />
                    </div>
                  </div>
                )
              }
            </div>
          ))}
        </div>
      </div>
      <div className="custom-drawer__footer">
        <div className="ant-col-md-6" style={{ marginTop: '0.5rem' }}>
          <div className="indent-barcode__wrapper" style={{ display: 'flex' }}>
            <Button
              type="primary"
              onClick={() => handleSubmit()}
              disabled={getGeneralConfigurationProductsLoading}
              loading={createLabelLoading || updateLabelGenerationLoading}
              icon={<SaveOutlined />}
            >
              {isUpdate ? 'Update' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </Fragment>

  );
};

const mapStateToProps = ({ UserReducers, LabelGenerationReducers, GeneralConfigurationReducers }) => ({
  user: UserReducers.user,
  getLabelGenerationLoading: LabelGenerationReducers.getLabelGenerationLoading,
  labelGeneration: LabelGenerationReducers.labelGeneration,
  updateLabelGenerationLoading: LabelGenerationReducers.updateLabelGenerationLoading,
  createLabelLoading: LabelGenerationReducers.createLabelLoading,
  getGeneralConfigurationProductsLoading: GeneralConfigurationReducers.getGeneralConfigurationProductsLoading,
  generalConfigurationProducts: GeneralConfigurationReducers.generalConfigurationProducts,

});

const mapDispatchToProps = (dispatch) => ({
  getUserProfile: () => dispatch(UserActions.getUserProfile()),
  getLabelGeneration: (payload, callback) => dispatch(LabelGenerationActions.getLabelGeneration(payload, callback)),
  updateLabelGeneration: (payload, callback) => dispatch(LabelGenerationActions.updateLabelGeneration(payload, callback)),
  createLabel: (payload, callback) => dispatch(LabelGenerationActions.createLabel(payload, callback)),
  getGeneralConfiguration: (entityType, callback) => dispatch(generalConfigurationActions.getGeneralConfiguration(entityType, callback)),

});

LabelGenerationForm.propTypes = {
  user: PropTypes.any,
  selectedOrgLabels: PropTypes.any,
  selectedLabel: PropTypes.any,
  callback: PropTypes.func,
  updateLabelGeneration: PropTypes.any,
  updateLabelGenerationLoading: PropTypes.bool,
  createLabelLoading: PropTypes.bool,
  createLabel: PropTypes.func,
  isUpdate: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(LabelGenerationForm));
